class ShowroomsMap {
  constructor(root, showrooms) {
    this.root = root;
    this.showrooms = showrooms
      .filter(
        (showroom) =>
          showroom.lat !== null &&
          showroom.lat !== undefined &&
          String(showroom.lat).trim() !== "" &&
          showroom.lng !== null &&
          showroom.lng !== undefined &&
          String(showroom.lng).trim() !== "",
      )
      .map((showroom) => ({
        ...showroom,
        id: Number(showroom.id),
        lat: Number(showroom.lat),
        lng: Number(showroom.lng),
      }))
      .filter((showroom) => Number.isFinite(showroom.lat) && Number.isFinite(showroom.lng));
    this.mapElement = root.querySelector("#showrooms-map");
    this.mapWrapper = root.querySelector(".showrooms-map-wrapper");
    this.items = Array.from(root.querySelectorAll(".showrooms-list .showroom-item"));
    this.selectedMobile = root.querySelector(".showrooms-map-selected-mobile");
    this.map = null;
    this.markers = [];
    this.activeMarker = null;
    this.activeShowroom = null;
    this.customInfoBox = null;
    this.mapListener = null;
    this.visibleIds = this.items
      .filter((item) => !item.classList.contains("hidden"))
      .map((item) => Number(item.dataset.id));
    this.desktopState = this.isDesktopView();

    if (this.mapElement && this.mapWrapper && window.maplibregl?.Map) this.init();
  }

  init() {
    this.initMap();
    this.createCustomInfoBox();
    this.attachSearchListener();
    this.attachViewListener();
    this.attachResizeListener();

    this.map.once("load", () => {
      this.applyMapStyle();
      this.createMarkers();
      this.attachItemListeners();
      this.updateVisibleMarkers(this.visibleIds, false);
    });
  }

  initMap() {
    this.map = new maplibregl.Map({
      container: this.mapElement,
      style: "https://tiles.openfreemap.org/styles/positron",
      center: [19.1, 52],
      zoom: this.isDesktopView() ? 5.45 : 5.1,
      attributionControl: true,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      maxPitch: 0,
    });
  }

  applyMapStyle() {
    const paint = (layerId, property, value) => {
      if (this.map.getLayer(layerId)) this.map.setPaintProperty(layerId, property, value);
    };
    const locale = document.documentElement.lang.split("-")[0];
    const localizedName = [
      "coalesce",
      ["get", `name:${locale}`],
      ["get", "name"],
      ["get", "name_en"],
    ];

    paint("background", "background-color", "#f8f8f6");
    paint("park", "fill-color", "#f3f3f0");
    paint("water", "fill-color", "#f1f2f0");
    paint("landuse_residential", "fill-color", "#f6f6f3");
    paint("landcover_wood", "fill-color", "#f0f1ed");
    paint("waterway", "line-color", "#dedfdb");
    paint("boundary_2", "line-color", "#a6a6a2");
    paint("boundary_3", "line-color", "#c8c8c4");
    paint("boundary_disputed", "line-color", "#b7b7b3");

    ["label_state"].forEach((layerId) => {
      if (this.map.getLayer(layerId)) this.map.setLayoutProperty(layerId, "visibility", "none");
    });

    this.map.getStyle().layers.forEach((layer) => {
      if (layer.type === "symbol") {
        const textField = layer.layout?.["text-field"];

        if (textField && JSON.stringify(textField).includes('"name')) {
          this.map.setLayoutProperty(layer.id, "text-field", localizedName);
        }

        if (!layer.id.startsWith("label_") && !layer.id.startsWith("water_name")) {
          this.map.setLayoutProperty(layer.id, "visibility", "none");
        }
      }

      if (layer.type === "line" && layer["source-layer"] === "transportation") {
        if (layer.paint?.["line-color"] !== undefined) {
          this.map.setPaintProperty(layer.id, "line-color", "#dededb");
        }

        if (layer.paint?.["line-opacity"] !== undefined) {
          this.map.setPaintProperty(layer.id, "line-opacity", 0.55);
        }
      }
    });

    [
      "label_other",
      "label_village",
      "label_town",
      "label_state",
      "label_city",
      "label_city_capital",
      "label_country_1",
      "label_country_2",
      "label_country_3",
    ].forEach((layerId) => {
      paint(layerId, "text-color", "#656565");
      paint(layerId, "text-halo-color", "rgba(255, 255, 255, 0.92)");
    });
  }

  createCustomInfoBox() {
    this.customInfoBox = document.createElement("div");
    this.customInfoBox.className = "showroom-custom-infobox";
    this.mapWrapper.appendChild(this.customInfoBox);
  }

  createMarkers() {
    this.showrooms.forEach((showroom) => {
      const element = this.createPinElement(false, showroom.name);
      const marker = new maplibregl.Marker({
        element,
        anchor: "bottom",
      })
        .setLngLat([showroom.lng, showroom.lat])
        .addTo(this.map);

      marker.showroom = showroom;
      marker.showroomId = showroom.id;
      marker.isActive = false;
      marker.isVisible = true;
      element.addEventListener("click", () => this.handleMarkerClick(marker, showroom));
      this.markers.push(marker);
    });
  }

  createPinElement(isActive, label) {
    const pin = document.createElement("button");

    pin.type = "button";
    pin.setAttribute("aria-label", label);
    pin.title = label;
    this.updatePinElement(pin, isActive);

    return pin;
  }

  updatePinElement(pin, isActive) {
    const isDesktop = this.isDesktopView();
    const dimensions = isDesktop
      ? isActive
        ? { width: 66, height: 64 }
        : { width: 40, height: 39 }
      : { width: 34, height: 33 };
    const fill = isActive ? (isDesktop ? "#B2A1CD" : "#A1B296") : "#FFFFFF";

    pin.classList.add("showroom-map-pin");
    pin.classList.toggle("showroom-map-pin--active", isActive);
    pin.setAttribute("aria-pressed", String(isActive));
    pin.innerHTML = `
      <svg
        width="${dimensions.width}"
        height="${dimensions.height}"
        viewBox="0 0 76 74"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M66.5 30.833C66.5 52.417 38 70.917 38 70.917S9.5 52.417 9.5 30.833c0-7.36 3.003-14.418 8.348-19.622C23.192 6.007 30.441 3.083 38 3.083s14.808 2.924 20.153 8.128C63.497 16.415 66.5 23.474 66.5 30.833Z"
          fill="${fill}"
          stroke="#1E1E1E"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M38 40.083c5.247 0 9.5-4.141 9.5-9.25s-4.253-9.25-9.5-9.25-9.5 4.141-9.5 9.25 4.253 9.25 9.5 9.25Z"
          fill="${fill}"
          stroke="#1E1E1E"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  isDesktopView() {
    return window.innerWidth >= 992;
  }

  handleMarkerClick(marker, showroom) {
    if (marker.isActive) {
      this.deactivateMarker(marker);
      return;
    }

    if (this.activeMarker) this.deactivateMarker(this.activeMarker);
    this.activateMarker(marker, showroom, { scrollItem: this.isDesktopView() });
  }

  handleItemSelect(showroomId, { focusMarker = false } = {}) {
    const marker = this.findMarker(showroomId);
    if (!marker?.isVisible) return;

    if (this.activeMarker !== marker) {
      if (this.activeMarker) this.deactivateMarker(this.activeMarker);
      this.activateMarker(marker, marker.showroom);
    }

    this.showMarkerOnMap(marker, focusMarker);
  }

  showMarkerOnMap(marker, focusMarker = false) {
    const moveMap = () => {
      this.map.resize();
      this.map.easeTo({
        center: marker.getLngLat(),
        duration: 350,
      });
    };

    if (this.isDesktopView()) {
      moveMap();
      return;
    }

    const mapTab = this.root.querySelector('[data-tab="map"]');
    if (mapTab && !mapTab.classList.contains("active")) mapTab.click();
    requestAnimationFrame(() => {
      moveMap();
      if (focusMarker) marker.getElement().focus({ preventScroll: true });
    });
  }

  activateMarker(marker, showroom, { scrollItem = false } = {}) {
    this.updatePinElement(marker.getElement(), true);
    marker.isActive = true;
    marker.getElement().style.zIndex = "10";
    this.activeMarker = marker;
    this.activeShowroom = showroom;
    this.setActiveItem(showroom.id, scrollItem);

    this.renderInfoBox(showroom);
    this.showMobileSelected(showroom);

    if (this.isDesktopView()) {
      this.customInfoBox.style.display = "block";
      requestAnimationFrame(() => this.positionInfoBox(marker));
    }

    if (this.mapListener) this.map.off("move", this.mapListener);
    this.mapListener = () => {
      if (this.activeMarker && this.isDesktopView()) this.positionInfoBox(this.activeMarker);
    };
    this.map.on("move", this.mapListener);
  }

  renderInfoBox(showroom) {
    const sourceItem = this.findItem(showroom.id);
    this.customInfoBox.replaceChildren();

    if (!sourceItem) return;

    const item = sourceItem.cloneNode(true);
    this.prepareClonedItem(item);
    item.classList.add("showroom-item-infobox");
    this.prepareInfoBoxItem(item);
    this.customInfoBox.appendChild(item);
  }

  prepareClonedItem(item) {
    item.classList.remove("hidden", "showroom-item--active", "showroom-item--interactive");
    item.removeAttribute("aria-current");
    item.removeAttribute("aria-hidden");
    item.querySelector(".showroom-item__select")?.remove();
  }

  prepareInfoBoxItem(item) {
    item.querySelector(".showroom-item__details")?.remove();
    item.querySelector(".showroom-item__actions")?.remove();
  }

  positionInfoBox(marker) {
    if (!marker || !this.customInfoBox.firstElementChild) return;

    const markerPoint = this.map.project(marker.getLngLat());
    const maxLeft = this.mapElement.offsetWidth - this.customInfoBox.offsetWidth - 8;
    const maxTop = this.mapElement.offsetHeight - this.customInfoBox.offsetHeight - 1;
    const left = Math.min(Math.max(8, markerPoint.x + 16), maxLeft);
    const top = Math.min(Math.max(8, markerPoint.y + 4), maxTop);

    this.customInfoBox.style.left = `${left}px`;
    this.customInfoBox.style.top = `${top}px`;
  }

  deactivateMarker(marker) {
    this.updatePinElement(marker.getElement(), false);
    marker.isActive = false;
    marker.getElement().style.zIndex = "";
    this.activeMarker = null;
    this.activeShowroom = null;
    this.setActiveItem(null);
    this.customInfoBox.style.display = "none";
    this.customInfoBox.replaceChildren();

    if (this.mapListener) {
      this.map.off("move", this.mapListener);
      this.mapListener = null;
    }

    if (this.selectedMobile) {
      this.selectedMobile.classList.remove("active");
      this.selectedMobile.replaceChildren();
    }
  }

  showMobileSelected(showroom) {
    if (!this.selectedMobile) return;

    const sourceItem = this.findItem(showroom.id);
    if (!sourceItem) return;

    const item = sourceItem.cloneNode(true);
    this.prepareClonedItem(item);
    item.classList.add("showroom-item--selected");
    this.selectedMobile.replaceChildren(item);
    this.selectedMobile.classList.add("active");
  }

  attachItemListeners() {
    this.items.forEach((item) => {
      const showroomId = Number(item.dataset.id);
      if (!this.findMarker(showroomId)) return;

      const selectButton = document.createElement("button");
      selectButton.type = "button";
      selectButton.className = "showroom-item__select";
      selectButton.setAttribute("aria-label", item.dataset.name || "");
      selectButton.setAttribute("aria-pressed", "false");
      selectButton.addEventListener("click", () => {
        this.handleItemSelect(showroomId, { focusMarker: true });
      });

      item.classList.add("showroom-item--interactive");
      item.prepend(selectButton);
      item.addEventListener("click", (event) => {
        if (event.target.closest("a, button")) return;
        this.handleItemSelect(showroomId);
      });
    });
  }

  setActiveItem(showroomId, scrollIntoView = false) {
    let activeItem = null;

    this.items.forEach((item) => {
      const isActive = Number(item.dataset.id) === Number(showroomId);
      item.classList.toggle("showroom-item--active", isActive);
      item.querySelector(".showroom-item__select")?.setAttribute("aria-pressed", String(isActive));

      if (isActive) {
        item.setAttribute("aria-current", "location");
        activeItem = item;
      } else {
        item.removeAttribute("aria-current");
      }
    });

    if (scrollIntoView && activeItem) {
      activeItem.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }

  findItem(showroomId) {
    return this.items.find((item) => Number(item.dataset.id) === Number(showroomId));
  }

  findMarker(showroomId) {
    return this.markers.find((marker) => Number(marker.showroomId) === Number(showroomId));
  }

  attachSearchListener() {
    this.root.addEventListener("showrooms:search", (event) => {
      const visibleIds = event.detail?.visibleIds || [];
      this.visibleIds = visibleIds;
      this.updateVisibleMarkers(visibleIds);
    });
  }

  attachViewListener() {
    this.root.addEventListener("showrooms:view", (event) => {
      if (event.detail?.view !== "map") return;

      requestAnimationFrame(() => {
        this.map.resize();
        if (this.activeMarker && this.isDesktopView()) this.positionInfoBox(this.activeMarker);
      });
    });
  }

  attachResizeListener() {
    let resizeFrame = null;

    window.addEventListener("resize", () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);

      resizeFrame = requestAnimationFrame(() => {
        const isDesktop = this.isDesktopView();

        if (isDesktop !== this.desktopState) {
          this.desktopState = isDesktop;
          this.refreshMarkerSizes();
        }

        this.map.resize();
        if (!this.activeMarker) return;

        if (isDesktop) {
          this.customInfoBox.style.display = "block";
          this.positionInfoBox(this.activeMarker);
        } else {
          this.customInfoBox.style.display = "none";
        }
      });
    });
  }

  refreshMarkerSizes() {
    this.markers.forEach((marker) => {
      this.updatePinElement(marker.getElement(), marker === this.activeMarker);
    });
  }

  updateVisibleMarkers(visibleIds, fitMap = true) {
    this.markers.forEach((marker) => {
      const isVisible = visibleIds.includes(marker.showroomId);

      if (isVisible && !marker.isVisible) marker.addTo(this.map);
      if (!isVisible && marker.isVisible) marker.remove();
      marker.isVisible = isVisible;
    });

    if (this.activeMarker && !visibleIds.includes(this.activeMarker.showroomId)) {
      this.deactivateMarker(this.activeMarker);
    }

    if (fitMap) this.fitMapToVisibleMarkers(visibleIds);

    if (!this.activeMarker && visibleIds.length) {
      const firstVisibleMarker = this.markers.find((marker) => visibleIds.includes(marker.showroomId));
      if (firstVisibleMarker) {
        this.activateMarker(firstVisibleMarker, firstVisibleMarker.showroom, {
          scrollItem: fitMap && this.isDesktopView(),
        });
      }
    }
  }

  fitMapToVisibleMarkers(visibleIds) {
    const visibleShowrooms = this.showrooms.filter((showroom) => visibleIds.includes(showroom.id));
    if (!visibleShowrooms.length) return;

    if (visibleShowrooms.length === 1) {
      this.map.jumpTo({
        center: [visibleShowrooms[0].lng, visibleShowrooms[0].lat],
        zoom: 12,
      });
      return;
    }

    const bounds = new maplibregl.LngLatBounds();
    visibleShowrooms.forEach((showroom) => {
      bounds.extend([showroom.lng, showroom.lat]);
    });
    this.map.fitBounds(bounds, {
      padding: 48,
      duration: 0,
      maxZoom: 12,
    });
  }
}
